import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useIntl } from 'react-intl';
import { CloseOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import type {
  MountSecondaryPanelConfig,
  SecondaryPanelConfig,
  SecondaryPanelProviderProps,
} from '@src/components/SecondaryPanel/context/SecondaryPanelContext';
import {
  DEFAULT_SECONDARY_PANEL_WIDTH,
  SecondaryPanelProvider,
  useSecondaryPanel,
  useSecondaryPanelFilters,
  useSecondaryPanelUI,
} from '@src/components/SecondaryPanel/context/SecondaryPanelContext';
import { SecondaryPanelRailPortalHost } from '@src/components/SecondaryPanel/SecondaryPanelRailPortalHost';
import {
  isFilterShortcutEvent,
  isMacPlatform,
} from '@src/components/SecondaryPanel/utils/filterShortcut';
import { getSecondaryPanelFrame } from '@src/components/SecondaryPanel/utils/getSecondaryPanelFrame';
import { getSecondaryPanelStickyCeiling } from '@src/components/SecondaryPanel/utils/getSecondaryPanelStickyCeiling';
import { isAntdOverlayOpen } from '@src/components/SecondaryPanel/utils/isAntdOverlayOpen';
import { useIsMobile } from '@src/packages/utils';

import './SecondaryPanel.less';

const EMPTY_FRAME = { top: 0, left: 0, maxHeight: 0, anchorHeight: 0 };

interface SecondaryPanelProps {
  className?: string;
}

interface SecondaryPanelPanelProps extends MountSecondaryPanelConfig {
  children: ReactNode;
}

interface SecondaryPanelContentProps {
  children: ReactNode;
  className?: string;
  /** Optional styles merged onto the content node (backwards-compatible). */
  style?: CSSProperties;
  shiftContent?: boolean;
}

const SecondaryPanelPanel: React.FC<SecondaryPanelPanelProps> = ({
  title,
  children,
  initialValues,
  width,
  onClose,
}) => {
  const { mountPanel, updatePanel, unmountPanel } = useSecondaryPanelUI();
  const initialValuesRef = useRef(initialValues);

  initialValuesRef.current = initialValues;

  useEffect(() => {
    mountPanel({
      title,
      width,
      onClose,
      initialValues: initialValuesRef.current,
    });

    return () => {
      unmountPanel();
    };
  }, [mountPanel, onClose, title, unmountPanel, width]);

  useEffect(() => {
    updatePanel({
      title,
      width,
      onClose,
      content: children,
    });
  }, [children, onClose, title, updatePanel, width]);

  return null;
};

type SecondaryPanelComponent = React.FC<SecondaryPanelProps> & {
  Panel: React.ComponentType<SecondaryPanelPanelProps>;
  Content: React.ComponentType<SecondaryPanelContentProps>;
  Provider: React.ComponentType<SecondaryPanelProviderProps>;
};

const SecondaryPanelContent: React.FC<SecondaryPanelContentProps> = ({
  children,
  className = '',
  style,
  shiftContent = true,
}) => {
  const { isOpen, panel, setContentNode } = useSecondaryPanelUI();
  const { isMobile } = useIsMobile();
  const panelWidth = panel.width ?? DEFAULT_SECONDARY_PANEL_WIDTH;
  const isShifted = Boolean(panel.content) && isOpen;
  const shouldShift = shiftContent !== false && isShifted && !isMobile;
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setContentNode(contentRef.current);

    return () => {
      setContentNode(null);
    };
  }, [setContentNode]);

  const contentStyle: CSSProperties = {
    ['--secondary-panel-width' as string]: `${panelWidth}px`,
    ...(shouldShift
      ? {
          paddingRight: panelWidth,
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }
      : {}),
    ...style,
  };

  return (
    <div
      ref={contentRef}
      className={classNames(
        'secondary-panel-content',
        shouldShift && 'secondary-panel-content--shifted',
        className,
      )}
      style={contentStyle}
      data-testid="secondary-panel-content"
    >
      {children}
    </div>
  );
};

const SecondaryPanelRoot: React.FC<SecondaryPanelProps> = ({ className = '' }) => {
  const intl = useIntl();
  const { isOpen, close, toggle, panel, contentNode, anchorNode } = useSecondaryPanelUI();
  const { isMobile } = useIsMobile();
  const resolvedTitle = panel.title ?? intl.formatMessage({ id: 'filterDropdown.title' });
  const panelWidth = panel.width ?? DEFAULT_SECONDARY_PANEL_WIDTH;
  const hasPanel = Boolean(panel.content);
  const isRailOpen = hasPanel && isOpen;
  const useMobileFullscreenHost = isMobile && hasPanel;
  const fullScreenMobile = isMobile && isRailOpen;
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasPanel) return undefined;

    const isMac = isMacPlatform();
    const handleFilterShortcut = (event: KeyboardEvent) => {
      if (!isFilterShortcutEvent(event, isMac) || isAntdOverlayOpen()) return;

      event.preventDefault();
      toggle();
    };

    window.addEventListener('keydown', handleFilterShortcut);
    return () => window.removeEventListener('keydown', handleFilterShortcut);
  }, [hasPanel, toggle]);

  const [frame, setFrame] = useState(EMPTY_FRAME);

  useEffect(() => {
    if (!fullScreenMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [fullScreenMobile]);

  useLayoutEffect(() => {
    if (isMobile) {
      setFrame(EMPTY_FRAME);
      return;
    }

    if (!contentNode || !hostRef.current?.parentElement) {
      return;
    }

    const updateFrame = () => {
      if (!contentNode || !hostRef.current?.parentElement) return;

      const parentRect = hostRef.current.parentElement.getBoundingClientRect();
      const contentRect = contentNode.getBoundingClientRect();
      const anchorRect = anchorNode?.getBoundingClientRect();
      const viewportTopMin = anchorNode
        ? 0
        : getSecondaryPanelStickyCeiling(contentNode, {
            minGap: 0,
            ignoreNode: hostRef.current,
            viewportHeight: window.innerHeight,
          });

      const panelFrame = getSecondaryPanelFrame({
        contentTop: anchorRect?.top ?? contentRect.top,
        parentTop: parentRect.top,
        topOffset: panel.topOffset ?? 0,
        viewportHeight: window.innerHeight,
        viewportBottomGap: 0,
        viewportTopMin,
      });

      setFrame({
        ...panelFrame,
        left: contentRect.left - parentRect.left,
        anchorHeight: anchorRect?.height ?? 0,
      });
    };

    updateFrame();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateFrame) : null;
    resizeObserver?.observe(contentNode);
    resizeObserver?.observe(hostRef.current.parentElement);
    if (anchorNode) resizeObserver?.observe(anchorNode);

    window.addEventListener('resize', updateFrame);
    window.addEventListener('scroll', updateFrame, true);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateFrame);
      window.removeEventListener('scroll', updateFrame, true);
    };
  }, [anchorNode, contentNode, isRailOpen, isMobile, panel.topOffset]);

  return (
    <div
      ref={hostRef}
      className={classNames(
        'secondary-panel-host',
        useMobileFullscreenHost && 'secondary-panel-host--mobile-fullscreen',
        isRailOpen && !fullScreenMobile && 'secondary-panel-host--rail-open',
        className,
      )}
      data-testid="secondary-panel-layout"
      style={
        useMobileFullscreenHost
          ? undefined
          : {
              top: frame.top,
              left: frame.left,
              height: frame.maxHeight || undefined,
              maxHeight: frame.maxHeight || undefined,
            }
      }
    >
      <aside
        className={classNames(
          'secondary-panel-host__rail',
          isRailOpen && 'secondary-panel-host__rail--open',
        )}
        style={{
          ['--secondary-panel-width' as string]: `${panelWidth}px`,
          ...(frame.anchorHeight
            ? { ['--secondary-panel-anchor-height' as string]: `${frame.anchorHeight}px` }
            : {}),
        }}
        aria-hidden={!isRailOpen}
        data-testid="secondary-panel-rail"
      >
        <div
          className={classNames(
            'secondary-panel-host__rail-inner',
            frame.anchorHeight > 0 && 'secondary-panel-host__rail-inner--flush-top',
          )}
        >
          <div className="secondary-panel-host__header">
            <span className="secondary-panel-host__title">{resolvedTitle}</span>
            <button
              type="button"
              className="secondary-panel-host__close"
              onClick={close}
              aria-label={intl.formatMessage({ id: 'filterDropdown.closePanel' })}
            >
              <CloseOutlined />
            </button>
          </div>
          <div className="secondary-panel-panel secondary-panel-panel--rail">
            <div className="secondary-panel-panel__body">{panel.content}</div>
          </div>
        </div>
      </aside>
    </div>
  );
};

const SecondaryPanel = Object.assign(SecondaryPanelRoot, {
  Panel: SecondaryPanelPanel,
  Content: SecondaryPanelContent,
  Provider: SecondaryPanelProvider,
}) as SecondaryPanelComponent;

export type { SecondaryPanelConfig };
export {
  SecondaryPanelProvider,
  useSecondaryPanel,
  useSecondaryPanelFilters,
  useSecondaryPanelUI,
  SecondaryPanelRailPortalHost,
};
export default SecondaryPanel;
